import React, { useEffect, useMemo, useState } from "react";
import { DataGridPremium, GridColDef, GridToolbar, useGridApiRef } from "@mui/x-data-grid-premium";
import { Button, Chip, Tooltip } from "@mui/joy";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getFetch, postFetch } from "../../../../../../fetch/fetch";
import totalLocationData from "../../../../../../data/locationdata";
import { tableUseStyle } from "../../../etc/tableusestyle";
import { API, toastOptions, errorText, EXCLUDED_LOCATIONS, Position, mmdd } from "./rosterutils";
import { StampButton, DateCell, SelectCell, TextCell, TodoFilterBar, fmtStamp, saveStamp, saveField, lossMode, severanceSuggest, isOffboardingComplete, toastResult } from "./stafftodocommon";

//퇴사직원 TODO — 퇴사일이 입력된 직무(+ 해제된 직무)가 대상. 검색어가 있으면 그 사람의 재직 중 직무도 보여줘 퇴사일을 입력할 수 있게
//직무해제 = 이 파트 퇴사 확정 → 다른 재직 파트가 없으면 CRM 비재원생 전환(서버 staffOffboard). 미서명 계약서는 자동 취소
const SEVERANCE_OPTIONS = [{ value: "O", label: "O" }, { value: "X", label: "X" }];

type Row = Position & { _siblings: Position[]; _lossMode: "na" | "naMulti" | "input"; _otherActiveCount: number; _suggest: "O" | "X" | null; _complete: boolean };

//서버 응답에 onboarding/offboarding 이 빠져 있어도(3단계 이전 서버) 빈 객체로
const normalize = (list: any[]): Position[] => (list || []).map((p: any) => ({ ...p, onboarding: p.onboarding || {}, offboarding: p.offboarding || {}, isManaged: p.isManaged === undefined ? 1 : p.isManaged }));

const StaffOffboarding: React.FC<any> = (props) => {

    const classes = tableUseStyle();
    const apiRef = useGridApiRef();

    const [positions, setPositions] = useState<Position[]>([]);
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState<"incomplete" | "all">("incomplete");
    const [branch, setBranch] = useState("all");
    const [search, setSearch] = useState<string>(props.initialSearch || "");
    const [busyId, setBusyId] = useState<number | null>(null);

    const branchOptions = totalLocationData.filter((each) =>
        props.user && each.academy.includes(props.user.academy) && !EXCLUDED_LOCATIONS.includes(each.english));

    useEffect(() => { load(true); }, []);

    const load = async (spinner: boolean) => {
        try {
            if (spinner) setLoading(true);
            const r: any = await getFetch(`${API}/roster`);
            if (r.message === "success") setPositions(normalize(r.positions));
            else toast.error(errorText(r, "목록을 불러오지 못했습니다"), toastOptions);
        } catch (e) {
            console.log(e);
        } finally {
            if (spinner) setLoading(false);
        }
    };
    const refresh = () => load(false);

    const rows: Row[] = useMemo(() => {
        const byUser: { [userId: number]: Position[] } = {};
        positions.forEach((p) => { (byUser[p.userId] = byUser[p.userId] || []).push(p); });
        return positions.filter((p) => p.isManaged === 1).map((p) => {
            const siblings = (byUser[p.userId] || []).filter((s) => s.id !== p.id);
            return {
                ...p,
                _siblings: siblings,
                _lossMode: lossMode(p, siblings),
                _otherActiveCount: siblings.filter((s) => s.isActive === 1).length,
                _suggest: severanceSuggest(p),
                _complete: isOffboardingComplete(p, siblings)
            };
        });
    }, [positions]);

    const visibleRows = useMemo(() => {
        const text = search.trim();
        return rows.filter((r) => {
            const inScope = !!r.endDate || r.isActive === 0 || (text && r.isActive === 1 && String(r.name || "").includes(text));
            if (!inScope) return false;
            if (status === "incomplete" && r._complete) return false;
            if (branch !== "all" && r.branchKey !== branch) return false;
            if (text && !String(r.name || "").includes(text)) return false;
            return true;
        });
    }, [rows, status, branch, search]);

    const field = async (row: Row, name: string, value: any) => {
        const r = await saveField(row.id, name, value);
        if (toastResult(r, null, "저장에 실패했습니다")) await refresh();
    };
    const stamp = async (row: Row, key: string, value: any) => {
        const r = await saveStamp(row.id, "offboarding", key, value);
        if (toastResult(r, null, "기록에 실패했습니다")) await refresh();
    };

    const release = async (row: Row) => {
        let text = `${row.name} · ${row.partName} — 이 파트 퇴사를 확정할까요? (퇴사일 ${row.endDate})`;
        if (row.contractState === "pending") text += "\n\n미서명 근로계약서가 있어 자동으로 취소됩니다.";
        text += row._otherActiveCount === 0
            ? "\n\n다른 재직 파트가 없어 CRM에서 비재원생으로 바뀝니다 (출입 등록 삭제)."
            : `\n\n다른 파트 ${row._otherActiveCount}개가 재직 중이라 재원 상태는 유지됩니다.`;
        if (!window.confirm(text)) return;
        try {
            setBusyId(row.id);
            const result: any = await postFetch(`${API}/positions/release`, { positionId: row.id });
            if (result.message === "success") {
                toast.success(result.userDeactivated ? "직무해제 · 비재원생 처리됨" : "직무해제 (다른 파트 재직중 → 재원 유지)", toastOptions);
                if (result.canceledDocs) toast.info(`미서명 계약서 ${result.canceledDocs}건 자동 취소`, toastOptions);
                if (result.warning === "fingerprintDeleteFailed") toast.error("BioStar 출입 삭제 실패 — 전체학생정보에서 확인하세요", toastOptions);
            } else {
                toastResult(result, null, "직무해제에 실패했습니다");
            }
            await refresh();
        } catch (e) {
            console.log(e);
            toast.error("오류가 발생했습니다", toastOptions);
        } finally {
            setBusyId(null);
        }
    };

    const naText = (mode: "na" | "naMulti" | "input") => (mode === "na" ? "해당없음" : mode === "naMulti" ? "해당없음(겸직)" : "");
    const sev = (row: Row) => (row.offboarding.severanceEligible ? String(row.offboarding.severanceEligible.value || "") : "");

    const columns: GridColDef[] = useMemo(() => [
        {
            field: "name", headerName: "이름", width: 110,
            renderCell: (params: any) => <span style={{ fontFamily: "Apple_SB" }}>{params.row.name}{params.row.isMultiple && <Chip size="sm" variant="soft" color="primary" sx={{ marginLeft: "4px" }}>겸직</Chip>}</span>
        },
        { field: "branchLabel", headerName: "지점", width: 80 },
        { field: "partName", headerName: "파트", width: 170, valueGetter: (params: any) => params.row.partName || "" },
        { field: "startDate", headerName: "입사일", width: 100, valueGetter: (params: any) => params.row.startDate || "" },
        {
            field: "endDate", headerName: "퇴사일", width: 150, valueGetter: (params: any) => params.row.endDate || "",
            //해제된 줄도 퇴사일은 고칠 수 있음 (비재원생 자동 해제는 퇴사일이 비어 있을 수 있어서)
            renderCell: (params: any) => <DateCell value={params.row.endDate || ""} onCommit={(v) => field(params.row, "endDate", v)} />
        },
        { field: "workDuration", headerName: "근무기간", width: 100 },
        //고보는 여기서 보기만 (수정은 신규직원 TODO·명부 줄 편집)
        { field: "employmentInsurance", headerName: "고보", width: 70, valueGetter: (params: any) => params.row.employmentInsurance || "미정" },
        {
            field: "memo", headerName: "비고", width: 160,
            renderCell: (params: any) => <TextCell value={params.row.memo || ""} width={150} onCommit={(v) => field(params.row, "memo", v)} />
        },
        {
            field: "lossInput", headerName: "상실신고 입력", width: 150,
            valueGetter: (params: any) => (params.row._lossMode !== "input" ? naText(params.row._lossMode) : (params.row.offboarding.lossInput ? params.row.offboarding.lossInput.value : "")),
            renderCell: (params: any) => <DateCell value={params.row.offboarding.lossInput ? params.row.offboarding.lossInput.value : ""} naText={naText(params.row._lossMode)} onCommit={(v) => stamp(params.row, "lossInput", v)} />
        },
        {
            field: "lossDone", headerName: "상실신고 이행", width: 150,
            valueGetter: (params: any) => (params.row._lossMode !== "input" ? naText(params.row._lossMode) : (params.row.offboarding.lossDone ? params.row.offboarding.lossDone.value : "")),
            renderCell: (params: any) => <DateCell value={params.row.offboarding.lossDone ? params.row.offboarding.lossDone.value : ""} naText={naText(params.row._lossMode)} onCommit={(v) => stamp(params.row, "lossDone", v)} />
        },
        {
            field: "salarySettled", headerName: "잔여급여 정산", width: 150,
            valueGetter: (params: any) => fmtStamp(params.row.offboarding.salarySettled),
            renderCell: (params: any) => <StampButton stamp={params.row.offboarding.salarySettled} label="정산" onSet={() => stamp(params.row, "salarySettled", true)} onClear={() => stamp(params.row, "salarySettled", null)} />
        },
        {
            field: "salaryTransferred", headerName: "잔여급여 이체", width: 150,
            valueGetter: (params: any) => fmtStamp(params.row.offboarding.salaryTransferred),
            renderCell: (params: any) => <StampButton stamp={params.row.offboarding.salaryTransferred} label="이체" onSet={() => stamp(params.row, "salaryTransferred", true)} onClear={() => stamp(params.row, "salaryTransferred", null)} />
        },
        {
            field: "severanceEligible", headerName: "퇴직금 해당", width: 190,
            valueGetter: (params: any) => sev(params.row),
            renderCell: (params: any) => {
                const row: Row = params.row;
                const saved = sev(row);
                const hint = row._suggest === "O" ? "1년 이상 → 자동 O" : row._suggest === "X" ? "1년 미만" : "";
                return (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                        <SelectCell value={saved} width={70} options={SEVERANCE_OPTIONS} placeholder={!saved && row._suggest ? row._suggest : undefined}
                            onCommit={(v) => stamp(row, "severanceEligible", v || null)} />
                        {!saved && hint && <span style={{ fontSize: "11px", color: "#9ca3af" }}>{hint}</span>}
                    </span>
                );
            }
        },
        {
            field: "severanceSettled", headerName: "퇴직금 정산", width: 150,
            valueGetter: (params: any) => (sev(params.row) === "O" ? fmtStamp(params.row.offboarding.severanceSettled) : "-"),
            renderCell: (params: any) => (sev(params.row) === "O"
                ? <StampButton stamp={params.row.offboarding.severanceSettled} label="정산" onSet={() => stamp(params.row, "severanceSettled", true)} onClear={() => stamp(params.row, "severanceSettled", null)} />
                : <span style={{ color: "#9ca3af" }}>-</span>)
        },
        {
            field: "severanceTransferred", headerName: "퇴직금 이체", width: 150,
            valueGetter: (params: any) => (sev(params.row) === "O" ? fmtStamp(params.row.offboarding.severanceTransferred) : "-"),
            renderCell: (params: any) => (sev(params.row) === "O"
                ? <StampButton stamp={params.row.offboarding.severanceTransferred} label="이체" onSet={() => stamp(params.row, "severanceTransferred", true)} onClear={() => stamp(params.row, "severanceTransferred", null)} />
                : <span style={{ color: "#9ca3af" }}>-</span>)
        },
        {
            field: "release", headerName: "직무해제", width: 260,
            valueGetter: (params: any) => (params.row.offboarding.released ? `해제됨${params.row.offboarding.released.auto ? "(비재원생 자동)" : ""} · ${mmdd(params.row.offboarding.released.doneAt)}${params.row.offboarding.released.userDeactivated || params.row.offboarding.released.auto ? " (비재원생)" : " (재원 유지)"}` : ""),
            renderCell: (params: any) => {
                const row: Row = params.row;
                const rel = row.offboarding.released;
                if (rel || row.isActive === 0) {
                    return (
                        <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                            <Button size="sm" variant="soft" color="success" sx={{ cursor: "default", fontSize: "12px" }}>{rel && rel.auto ? "해제됨(비재원생)" : "해제됨"} · {rel ? mmdd(rel.doneAt) : ""}</Button>
                            <span style={{ fontSize: "11px", color: "#6b7280" }}>{rel && rel.auto ? "이미 비재원생이라 자동 해제" : rel && rel.userDeactivated ? "비재원생 처리됨" : "다른 파트 재직중 → 재원 유지"}</span>
                        </span>
                    );
                }
                const btn = <span><Button size="sm" variant="soft" color="danger" disabled={!row.endDate} loading={busyId === row.id} onClick={() => release(row)}>직무해제</Button></span>;
                return !row.endDate ? <Tooltip title="퇴사일을 먼저 입력하세요" placement="top">{btn}</Tooltip> : btn;
            }
        }
    ], [busyId]);

    return (
        <div>
            <TodoFilterBar status={status} onStatus={setStatus} branch={branch} onBranch={setBranch} branchOptions={branchOptions} search={search} onSearch={setSearch}
                right={<span style={{ fontFamily: "Apple_R", fontSize: "12px", color: "#9ca3af" }}>전체학생정보에서 직원 줄 선택 후 "퇴원TODO" 누르면 이 화면으로 옴 · 겸직이면 파트별로 줄이 따로 · 표시 {visibleRows.length}줄</span>} />

            <div className={classes.root} style={{ height: "680px", width: "100%", backgroundColor: "white" }}>
                <DataGridPremium
                    rows={visibleRows}
                    columns={columns}
                    density="compact"
                    loading={loading}
                    apiRef={apiRef}
                    disableRowSelectionOnClick
                    getRowClassName={(params) => (params.row._complete ? "super-app-theme--처리완료" : "")}
                    initialState={{ pinnedColumns: { left: ["name"] } }}
                    slots={{ toolbar: GridToolbar }}
                    getRowHeight={() => 46}
                />
            </div>

            <div style={{ marginTop: "12px", padding: "12px 16px", background: "#fffbea", border: "1px solid #f5e6a8", borderRadius: "8px", fontFamily: "Apple_R", fontSize: "12px", color: "#6b7280", lineHeight: 1.7 }}>
                <b>직무해제</b> = 이 파트 퇴사 확정. 다른 파트도 없으면 CRM에서 <b>비재원생</b>으로 바뀜(출입 등록 삭제) · 미서명 계약서가 있으면 자동 취소 · <b>퇴직금 해당</b>은 1년 이상이면 자동 O로 제안되고 바꿀 수 있음 · 나머지 버튼은 처리 후 누르는 기록용(누른 사람·날짜 남음) ·
                퇴사일이 입력된 줄이 "미완료"에 남고, 직무해제까지 포함해 모든 항목이 끝나면 "전체"에서만 보임 · 고보가 X/해당X면 상실신고는 해당없음, 다른 파트가 계속 재직이면 해당없음(겸직)
            </div>
        </div>
    );
};

export default StaffOffboarding;
